import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Eye, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import TicketingLivePreview from '../components/ticketing/TicketingLivePreview';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useToast } from '../components/Toast';
import { ApiService } from '../utils/api';
import { useEventRoute } from '../utils/eventRef';
import { istInputToBackend, toIstInputValue } from '../utils/datetime';
import { IMAGE_LIMITS, resizeImage } from '../utils/imageResize';
import { mergeGroups, mergePasses } from '../utils/passes';
import { formatMoney } from '../utils/ticketing';

const PASS_TYPES = [['FREE', 'Free'], ['PAID', 'Paid'], ['APPROVAL', 'Approval based']];
const lines = (list) => (list || []).join('\n');
const toLines = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean);
const slugify = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '').slice(0, 50);

const fieldHasPrice = (field) => Number(field?.price) > 0
  || (field?.optionsWithPricing || []).some((option) => Number(option?.price) > 0
    || (option?.priceSchedule || []).some((tier) => Number(tier?.price) > 0));

const pricedFieldCount = (fields) => (fields || []).filter(fieldHasPrice).length;

const Section = ({ title, hint, children, action }) => (
  <section className="rounded-xl border bg-white">
    <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
      <div>
        <h2 className="font-semibold text-gray-900">{title}</h2>
        {hint && <p className="mt-0.5 text-sm text-gray-500">{hint}</p>}
      </div>
      {action}
    </div>
    <div className="space-y-4 p-5">{children}</div>
  </section>
);

const Field = ({ label, hint, children, className = '' }) => (
  <label className={`block text-sm ${className}`}>
    <span className="mb-1 block font-medium text-gray-800">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
  </label>
);

const TextArea = (props) => <textarea {...props} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />;

const Upload = ({ label, value, onChange, assetType, accept = 'image/jpeg,image/png,image/gif,image/webp' }) => {
  const [busy, setBusy] = useState(false);
  const { showError } = useToast();
  const video = /\.(mp4|webm)(\?|$)/i.test(value || '');
  return (
    <Field label={label}>
      {value && (video ? <video src={value} muted autoPlay loop playsInline className="mb-2 max-h-32 rounded border" />
        : <img src={value} alt="" className="mb-2 max-h-32 rounded border object-contain" />)}
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept={accept} className="max-w-full text-sm" disabled={busy} onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setBusy(true);
          try {
            const sized = file.type.startsWith('video/') ? file : await resizeImage(file, IMAGE_LIMITS[assetType]);
            onChange(await ApiService.uploadFormSchemaAsset(assetType, sized));
          } catch (err) {
            showError(err.message || 'Upload failed');
          } finally {
            setBusy(false);
          }
        }} />
        {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        {value && <Button type="button" variant="outline" size="sm" onClick={() => onChange(null)}>Remove</Button>}
      </div>
    </Field>
  );
};

const ListControls = ({ index, length, onMove, onRemove }) => (
  <div className="flex shrink-0 gap-1">
    <Button type="button" variant="ghost" size="icon" disabled={index === 0} onClick={() => onMove(index, -1)} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
    <Button type="button" variant="ghost" size="icon" disabled={index === length - 1} onClick={() => onMove(index, 1)} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
    <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(index)} aria-label="Remove"><Trash2 className="h-4 w-4 text-red-600" /></Button>
  </div>
);

const moveItem = (list, index, delta) => {
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(index + delta, 0, item);
  return next;
};

/** Event → Premium registration: landing page, pass groups, passes, form layout, information tabs. */
const TicketingSettingsPage = () => {
  const { eventId, eventRef } = useEventRoute();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [linking, setLinking] = useState(false);
  const [formSchema, setFormSchema] = useState(null);
  const { data: formSchemasResponse } = useQuery({ queryKey: ['formSchemas'], queryFn: () => ApiService.getFormSchemas() });
  const formSchemas = Array.isArray(formSchemasResponse?.content) ? formSchemasResponse.content : [];
  const { data: saved, isLoading } = useQuery({
    queryKey: ['ticketingConfig', eventId],
    queryFn: () => ApiService.getTicketingConfig(eventId),
    enabled: !!eventId,
  });
  const { data: event } = useQuery({ queryKey: ['event', eventId], queryFn: () => ApiService.getEvent(eventId), enabled: !!eventId });
  useEffect(() => { if (saved && !draft) setDraft(saved); }, [saved, draft]);
  useEffect(() => { if (event?.formSchema && !formSchema) setFormSchema(event.formSchema); }, [event, formSchema]);
  const dirty = useMemo(() => draft && JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);

  if (isLoading || !draft) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setList = (key, index, patch) => setDraft((d) => ({ ...d, [key]: d[key].map((item, i) => (i === index ? { ...item, ...patch } : item)) }));
  const move = (key) => (index, delta) => setDraft((d) => ({ ...d, [key]: moveItem(d[key], index, delta) }));
  const remove = (key) => (index) => setDraft((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== index) }));
  const linkForm = async (schemaId) => {
    if (!schemaId) return;
    const schema = formSchemas.find((item) => item.schemaId === schemaId);
    setLinking(true);
    try {
      const updated = await ApiService.linkEventForm(eventId, schemaId);
      queryClient.setQueryData(['event', eventId], updated);
      setFormSchema(updated?.formSchema || []);
      const current = draft || await ApiService.getTicketingConfig(eventId);
      const next = {
        ...current,
        passes: mergePasses(current.passes, schema?.passes || []),
        groups: schema?.groups?.some((group) => group?.name?.trim()) ? mergeGroups(current.groups, schema.groups) : current.groups,
        fields: [],
      };
      const savedConfig = await ApiService.updateTicketingConfig(eventId, next);
      queryClient.setQueryData(['ticketingConfig', eventId], savedConfig);
      setDraft(savedConfig);
      showSuccess(schema?.name ? `Linked ${schema.name}` : 'Form linked');
    } catch (err) {
      showError(err.message || 'Could not link this form');
    } finally {
      setLinking(false);
    }
  };

  const theme = draft.theme || {};
  const publicBase = `${window.location.origin}/register/${event?.slug || eventRef}`;

  const save = async () => {
    setSaving(true);
    try {
      const result = await ApiService.updateTicketingConfig(eventId, draft);
      queryClient.setQueryData(['ticketingConfig', eventId], result);
      queryClient.invalidateQueries({ queryKey: ['ticketing'] });
      setDraft(result);
      if (event && formSchema && JSON.stringify(formSchema) !== JSON.stringify(event.formSchema || [])) {
        const updatedEvent = await ApiService.updateEvent(eventId, { ...event, formSchema });
        queryClient.setQueryData(['event', eventId], updatedEvent);
        setFormSchema(updatedEvent?.formSchema || formSchema);
      }
      showSuccess('Premium registration saved');
    } catch (err) {
      showError(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1700px] pb-24 xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(440px,1fr)] xl:items-start xl:gap-6">
      <div className="min-w-0 space-y-5">
      <div>
        <Link to={`/admin/events/${eventRef}/settings`} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="mr-1 h-4 w-4" /> Event settings
        </Link>
        <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Premium registration</h1>
            <p className="text-sm text-gray-500">{event?.name} · landing page, passes, multi-attendee bookings and payments.</p>
          </div>
          <div className="flex items-center gap-4">
          <Button type="button" variant="outline" size="sm" className="xl:hidden" onClick={() => setPreviewExpanded(true)} disabled={!event}>
            <Eye className="mr-1 h-4 w-4" />Live preview
          </Button>
          {saved?.enabled && (
            <a href={publicBase} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline">
              Open public page <ExternalLink className="h-4 w-4" />
            </a>
          )}
          </div>
        </div>
      </div>

      <Section title="Status">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-medium">Use premium registration</p>
            <p className="text-sm text-gray-500">When off, the public page keeps the regular registration form.</p>
          </div>
          <Switch checked={!!draft.enabled} onCheckedChange={(v) => set({ enabled: v })} aria-label="Use premium registration" />
        </div>
        <div className="flex items-center justify-between gap-4 border-t pt-4">
          <div>
            <p className="font-medium">Verify email with a one-time code</p>
            <p className="text-sm text-gray-500">Each attendee confirms their email before the booking is accepted.</p>
          </div>
          <Switch checked={!!draft.verifyEmail} onCheckedChange={(v) => set({ verifyEmail: v })} aria-label="Verify email" />
        </div>
        <div className="flex items-center justify-between gap-4 border-t pt-4">
          <div>
            <p className="font-medium">On-spot desk</p>
            <p className="text-sm text-gray-500">Regular is the single-page form. Premium uses this page’s passes and booking form, and those registrations are marked on-spot. Turn premium registration on for that to apply.</p>
          </div>
          <select
            className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
            value={draft.onSpotFlow === 'PREMIUM' ? 'PREMIUM' : 'REGULAR'}
            onChange={(e) => set({ onSpotFlow: e.target.value })}
            aria-label="On-spot registration flow"
          >
            <option value="REGULAR">Regular registration</option>
            <option value="PREMIUM">Premium registration</option>
          </select>
        </div>
      </Section>

      <Section title="Form" hint="Build passes, categories, sections and field prices in Form builder, then link the form. This page adds the event details and can override prices that the form already has.">
        <Field label="Linked form">
          <select
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
            value={event?.selectedSchemaId || ''}
            disabled={linking}
            onChange={(e) => linkForm(e.target.value)}
          >
            <option value="">Select a form</option>
            {formSchemas.map((schema) => (
              <option key={schema.schemaId} value={schema.schemaId}>{schema.name}</option>
            ))}
          </select>
        </Field>
        <p className="text-sm text-gray-600">
          {linking ? 'Linking…' : 'Linking copies the form onto this event. New fields come from the form. A price you changed on this event stays, including when that field or option is renamed. A price you have not changed here follows the form. Sales dates, capacity and icons stay.'}
        </p>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to="/admin/settings/forms">Open form builder</Link>
        </Button>
        {draft.fields?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-gray-700">An older layout on this page is still overriding the form’s sections.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => set({ fields: [] })}>Use the form’s sections</Button>
          </div>
        )}
      </Section>

      <Section title="Branding" hint="Colours, banner (image, GIF or short MP4/WebM loop) and header art.">
        <div className="grid gap-4 sm:grid-cols-4">
          {[['primaryColor', 'Primary'], ['accentColor', 'Accent'], ['backgroundColor', 'Page background'], ['headerColor', 'Header']].map(([key, label]) => (
            <Field key={key} label={label}>
              <div className="flex items-center gap-2">
                <input type="color" value={theme[key] || '#1f4b43'} onChange={(e) => set({ theme: { ...theme, [key]: e.target.value } })} className="h-10 w-12 rounded border" />
                <Input value={theme[key] || ''} placeholder="default" onChange={(e) => set({ theme: { ...theme, [key]: e.target.value } })} />
              </div>
            </Field>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Upload label="Banner" assetType="ticketing_banner" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm" value={draft.bannerUrl} onChange={(v) => set({ bannerUrl: v })} />
          <Upload label="Header art (right side of the page header)" assetType="ticketing_art" value={theme.headerArtUrl} onChange={(v) => set({ theme: { ...theme, headerArtUrl: v } })} />
          <p className="text-xs text-gray-500 sm:col-span-2">Banner: use a wide image, about 1600 × 500 px. Taller images are shown smaller so nothing gets cropped.</p>
        </div>
        <Field label="Extra footer logos" hint="One image URL per line. Show, hide and uploaded logos are in Event settings → Registration.">
          <TextArea rows={2} value={lines(draft.footerLogos)} onChange={(e) => set({ footerLogos: e.target.value.split('\n') })} onBlur={(e) => set({ footerLogos: toLines(e.target.value) })} />
        </Field>
      </Section>

      <Section title="Event information">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Header line" hint="e.g. 2 - 5 Nov 2026 | Bharat Mandapam"><Input value={draft.tagline || ''} onChange={(e) => set({ tagline: e.target.value })} /></Field>
          <Field label="Venue name"><Input value={draft.venueName || ''} onChange={(e) => set({ venueName: e.target.value })} /></Field>
          <Field label="Venue address"><Input value={draft.venueAddress || ''} onChange={(e) => set({ venueAddress: e.target.value })} /></Field>
          <Field label="Map link" hint="Optional. A Google Maps link or its Share → Embed a map URL; leave empty to map the venue name and address."><Input value={draft.mapUrl || ''} placeholder="https://maps.google.com/..." onChange={(e) => set({ mapUrl: e.target.value })} /></Field>
          <Field label="Contact email"><Input value={draft.contactEmail || ''} onChange={(e) => set({ contactEmail: e.target.value })} /></Field>
          <Field label="Contact phone"><Input value={draft.contactPhone || ''} onChange={(e) => set({ contactPhone: e.target.value })} /></Field>
          <Field label="Website"><Input value={draft.websiteUrl || ''} placeholder="https://" onChange={(e) => set({ websiteUrl: e.target.value })} /></Field>
        </div>
        <Field label="Text above the pass categories"><TextArea rows={2} value={draft.intro || ''} onChange={(e) => set({ intro: e.target.value })} /></Field>
        <Field label="Order summary note" hint="Emails and links become clickable."><TextArea rows={2} value={draft.note || ''} onChange={(e) => set({ note: e.target.value })} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Terms & Conditions link"><Input value={draft.termsUrl || ''} placeholder="https://" onChange={(e) => set({ termsUrl: e.target.value })} /></Field>
          <Field label="Privacy Policy link"><Input value={draft.privacyUrl || ''} placeholder="https://" onChange={(e) => set({ privacyUrl: e.target.value })} /></Field>
        </div>
      </Section>

      <Section title="Field prices" hint="Only fields that already have a price on the form. Saving writes the new amounts onto this event. Linking the form again keeps these amounts.">
        {pricedFieldCount(formSchema) === 0 && <p className="text-sm text-gray-500">This form has no priced fields yet. Add a price on a dropdown option or a list field in Form builder, then link the form.</p>}
        {(formSchema || []).map((field, index) => !fieldHasPrice(field) ? null : (
          <div key={field.id || field.label || index} className="rounded-lg border p-4">
            <p className="font-medium text-gray-900">{field.label || 'Untitled field'}</p>
            {Number(field.price) > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <span className="w-28 text-sm text-gray-600">Field price</span>
                <Input className="w-32" type="number" min="0" step="0.01" value={field.price}
                  onChange={(e) => setFormSchema((fields) => fields.map((item, i) => i === index ? { ...item, price: Number(e.target.value) || 0 } : item))} />
                <span className="text-sm text-gray-500">{field.currency || 'INR'}</span>
              </div>
            )}
            {(field.optionsWithPricing || []).map((option, optionIndex) => (Number(option.price) > 0 || (option.priceSchedule || []).some((tier) => Number(tier.price) > 0)) && (
              <div key={option.id || option.label || optionIndex} className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-28 truncate text-sm text-gray-700">{option.label || 'Option'}</span>
                  <Input className="w-32" type="number" min="0" step="0.01" value={option.price ?? 0}
                    onChange={(e) => setFormSchema((fields) => fields.map((item, i) => i === index ? {
                      ...item,
                      optionsWithPricing: item.optionsWithPricing.map((opt, j) => j === optionIndex ? { ...opt, price: Number(e.target.value) || 0 } : opt),
                    } : item))} />
                  <span className="text-sm text-gray-500">{option.currency || field.currency || 'INR'}</span>
                </div>
                {(option.priceSchedule || []).map((tier, tierIndex) => Number(tier.price) > 0 && (
                  <div key={tier.fromDate || tierIndex} className="flex items-center gap-2 pl-6">
                    <span className="w-28 text-sm text-gray-500">From {tier.fromDate}</span>
                    <Input className="w-32" type="number" min="0" step="0.01" value={tier.price}
                      onChange={(e) => setFormSchema((fields) => fields.map((item, i) => i === index ? {
                        ...item,
                        optionsWithPricing: item.optionsWithPricing.map((opt, j) => j === optionIndex ? {
                          ...opt,
                          priceSchedule: opt.priceSchedule.map((row, k) => k === tierIndex ? { ...row, price: Number(e.target.value) || 0 } : row),
                        } : opt),
                      } : item))} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </Section>

      <Section title="Days" hint="Columns of the day-access grid (Day 1, Day 2 ...)." action={<Button type="button" size="sm" variant="outline" onClick={() => set({ days: [...draft.days, `Day ${draft.days.length + 1}`] })}><Plus className="mr-1 h-4 w-4" />Day</Button>}>
        <div className="flex flex-wrap gap-2">
          {draft.days.map((day, i) => (
            <div key={i} className="flex items-center gap-1">
              <Input className="w-28" value={day} onChange={(e) => set({ days: draft.days.map((d, j) => (j === i ? e.target.value : d)) })} />
              <Button type="button" variant="ghost" size="icon" onClick={() => set({ days: draft.days.filter((_, j) => j !== i) })} aria-label="Remove day"><Trash2 className="h-4 w-4 text-red-600" /></Button>
            </div>
          ))}
          {draft.days.length === 0 && <p className="text-sm text-gray-500">No day grid.</p>}
        </div>
      </Section>

      <Section title="Passes" hint="Free, Paid or Approval comes from the form. Day access is set here.">
        {draft.groups.length === 0 && <p className="text-sm text-gray-500">Choose a form in Linked form at the top of this page. A pass such as Delegate can hold several priced categories.</p>}
        {draft.groups.map((group, i) => (
          <div key={group.id || i} className="rounded-lg border p-4">
            <p className="font-medium text-gray-900">{group.name || 'Unnamed pass'}</p>
            <p className="text-sm text-gray-500">{PASS_TYPES.find(([value]) => value === (group.type || 'PAID'))?.[1] || group.type}{group.description ? ` · ${group.description}` : ''}</p>
            {draft.days.length > 0 && (
              <div className="mt-3 grid gap-2 sm:grid-cols-4">
                {draft.days.map((day, d) => (
                  <Field key={d} label={day}>
                    <Input value={group.dayAccess?.[d] || ''} placeholder="Full Day / No access" onChange={(e) => {
                      const access = [...(group.dayAccess || [])];
                      while (access.length < draft.days.length) access.push('');
                      access[d] = e.target.value;
                      setList('groups', i, { dayAccess: access });
                    }} />
                  </Field>
                ))}
              </div>
            )}
          </div>
        ))}
      </Section>

      <Section title="Categories" hint="Name and price come from the linked form. Sales dates, capacity, benefits and the icon are set here.">
        {draft.passes.length === 0 && <p className="text-sm text-gray-500">No categories yet. Add them under a pass in Form builder, then link that form.</p>}
        {draft.passes.map((pass, i) => {
          const link = `${publicBase}/book/${pass.id || slugify(pass.name)}`;
          const category = draft.groups.find((group) => (group.id || slugify(group.name)) === pass.groupId);
          return (
            <div key={pass.id || i} className="rounded-lg border p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium text-gray-900">{pass.name || 'Unnamed category'}</p>
                <p className="text-sm text-gray-600">
                  {category ? `${category.name} · ` : ''}
                  {PASS_TYPES.find(([value]) => value === (category?.type || pass.type || 'PAID'))?.[1]}
                  {(category?.type || pass.type) === 'PAID' ? ` · ${formatMoney(pass.price || 0, pass.currency || event?.currency || 'INR')}` : ''}
                </p>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <Field label="Attendees per booking"><Input type="number" min="1" max="50" value={pass.maxPerBooking ?? 1} onChange={(e) => setList('passes', i, { maxPerBooking: Number(e.target.value) || 1 })} /></Field>
                  <Field label="Capacity" hint="Empty = unlimited"><Input type="number" min="0" value={pass.capacity ?? ''} onChange={(e) => setList('passes', i, { capacity: e.target.value === '' ? null : Number(e.target.value) })} /></Field>
                  <Field label="Sales open (IST)"><Input type="datetime-local" value={toIstInputValue(pass.salesStartAt)} onChange={(e) => setList('passes', i, { salesStartAt: istInputToBackend(e.target.value) })} /></Field>
                  <Field label="Sales close (IST)"><Input type="datetime-local" value={toIstInputValue(pass.salesEndAt)} onChange={(e) => setList('passes', i, { salesEndAt: istInputToBackend(e.target.value) })} /></Field>
                </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Field label="Benefits" hint="One per line; the first three show on the card">
                  <TextArea rows={4} value={lines(pass.benefits)} onChange={(e) => setList('passes', i, { benefits: e.target.value.split('\n') })} onBlur={(e) => setList('passes', i, { benefits: toLines(e.target.value) })} />
                </Field>
                <div className="space-y-3">
                  <Field label="Description"><TextArea rows={2} value={pass.description || ''} onChange={(e) => setList('passes', i, { description: e.target.value })} /></Field>
                  <Upload label="Icon" assetType="ticketing_icon" value={pass.iconUrl} onChange={(v) => setList('passes', i, { iconUrl: v })} />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!pass.accessCodeRequired} onChange={(e) => setList('passes', i, { accessCodeRequired: e.target.checked })} />Access code required</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!pass.hidden || !pass.groupId} disabled={!pass.groupId} onChange={(e) => setList('passes', i, { hidden: e.target.checked })} />Hidden (link only)</label>
                <span className="min-w-0 truncate text-xs text-gray-500">Direct link: <a href={link} target="_blank" rel="noreferrer" className="text-blue-600">{link}</a></span>
              </div>
            </div>
          );
        })}
      </Section>

      <Section title="Information tabs" hint="About, Venue, Terms, FAQ ... under the registration card."
        action={<Button type="button" size="sm" variant="outline" onClick={() => set({ tabs: [...draft.tabs, { title: '', content: '' }] })}><Plus className="mr-1 h-4 w-4" />Tab</Button>}>
        {draft.tabs.map((tab, i) => (
          <div key={i} className="flex items-start gap-3 rounded-lg border p-3">
            <div className="flex-1 space-y-2">
              <Input placeholder="Title" value={tab.title || ''} onChange={(e) => setList('tabs', i, { title: e.target.value })} />
              <TextArea rows={4} placeholder="Text. Leave a blank line between paragraphs." value={tab.content || ''} onChange={(e) => setList('tabs', i, { content: e.target.value })} />
            </div>
            <ListControls index={i} length={draft.tabs.length} onMove={move('tabs')} onRemove={remove('tabs')} />
          </div>
        ))}
      </Section>

      <Section title="Instructions popup" hint="Shown once when someone opens the passes.">
        <Field label="Title"><Input value={draft.instructions?.title || ''} placeholder="General instructions" onChange={(e) => set({ instructions: { ...(draft.instructions || {}), title: e.target.value } })} /></Field>
        <Field label="Points" hint="One per line; leave empty for no popup">
          <TextArea rows={3} value={lines(draft.instructions?.items)} onChange={(e) => set({ instructions: { ...(draft.instructions || {}), items: e.target.value.split('\n') } })} />
        </Field>
      </Section>

      </div>

      <TicketingLivePreview event={event} draft={draft} expanded={previewExpanded} onExpandedChange={setPreviewExpanded} dirty={dirty} />

      {dirty && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 px-4 py-3 backdrop-blur lg:pl-64">
          <div className="mx-auto flex max-w-5xl items-center justify-end gap-3">
            <span className="text-sm text-gray-600">Unsaved changes</span>
            <Button variant="outline" onClick={() => setDraft(saved)} disabled={saving}>Discard</Button>
            <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketingSettingsPage;
